/**
 * A single entry inside `simulation_metadata_input.script_turns`.
 *
 * Wire format:
 *   - Legacy text turn: a bare string. Frontends should keep writing bare
 *     strings for text turns so existing all-string payloads stay byte-identical.
 *   - DTMF (keypress) turn: { type: "dtmf", digits: string }.
 *   - Explicit text dict form: { type: "text", text: string } — accepted by the
 *     parser but the FE prefers the bare string shape.
 *   - Unknown turn types (e.g. a future `{type: "wait"}`) are preserved as-is
 *     and rendered read-only, so saving does not drop forward-compatible data.
 */
export type ScriptTurn = string | ScriptTurnDict

export type ScriptTurnDict =
  | TextScriptTurnDict
  | DtmfScriptTurn
  | SkipScriptTurn
  | UnknownScriptTurn

export interface TextScriptTurnDict {
  type: "text"
  text: string
}

export interface DtmfScriptTurn {
  type: "dtmf"
  digits: string
}

/**
 * Silent turn: the persona says nothing for one turn. Named explicitly (not
 * left to UnknownScriptTurn) so generated transport types with a closed
 * `{ type: "skip" }` shape stay assignable; rendering still treats it as a
 * read-only foreign entry.
 */
export interface SkipScriptTurn {
  type: "skip"
}

/**
 * Catch-all for forward-compat turn dicts that don't match a known shape
 * (legacy unrecognized entry, future `{type: "wait"}` etc.). Keeping this
 * loose lets the parser preserve foreign entries instead of dropping them.
 */
export type UnknownScriptTurn = Record<string, unknown>

type ScriptTurnKind = "text" | "dtmf" | "unknown"

export const DTMF_MAX_DIGITS = 32
const DTMF_DIGIT_REGEX = /^[0-9*#]+$/

function isTextScriptTurn(
  turn: ScriptTurn,
): turn is string | TextScriptTurnDict {
  if (typeof turn === "string") {
    return true
  }
  return (
    turn.type === "text" &&
    typeof (turn as TextScriptTurnDict).text === "string"
  )
}

export function isDtmfScriptTurn(turn: ScriptTurn): turn is DtmfScriptTurn {
  return (
    typeof turn === "object" &&
    turn !== null &&
    turn.type === "dtmf" &&
    typeof (turn as DtmfScriptTurn).digits === "string"
  )
}

export function getScriptTurnKind(turn: ScriptTurn): ScriptTurnKind {
  if (isTextScriptTurn(turn)) {
    return "text"
  }
  if (isDtmfScriptTurn(turn)) {
    return "dtmf"
  }
  return "unknown"
}

/** Read the displayable text for a turn, or null if it isn't a text turn. */
export function getScriptTurnText(turn: ScriptTurn): string | null {
  if (typeof turn === "string") {
    return turn
  }
  if (
    turn.type === "text" &&
    typeof (turn as TextScriptTurnDict).text === "string"
  ) {
    return (turn as TextScriptTurnDict).text
  }
  return null
}

/** Update a text turn while preserving its existing shape (bare string vs dict). */
export function setScriptTurnText(turn: ScriptTurn, text: string): ScriptTurn {
  if (typeof turn === "string") {
    return text
  }
  if (turn.type === "text") {
    return { ...(turn as TextScriptTurnDict), text }
  }
  return text
}

interface DtmfValidationResult {
  /** Hard error: empty or contains characters outside 0-9 * #. */
  error: string | null
  /** Soft warning: digits longer than DTMF_MAX_DIGITS. */
  warning: string | null
}

export function validateDtmfDigits(digits: string): DtmfValidationResult {
  if (digits.length === 0) {
    return { error: "Enter at least one digit.", warning: null }
  }
  if (!DTMF_DIGIT_REGEX.test(digits)) {
    return {
      error: "Only digits 0–9, * and # are allowed.",
      warning: null,
    }
  }
  if (digits.length > DTMF_MAX_DIGITS) {
    return {
      error: null,
      warning: `Sequences over ${DTMF_MAX_DIGITS} digits are unusually long.`,
    }
  }
  return { error: null, warning: null }
}
